import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { fetchFips, fetchPrinciples, fetchResources, fetchResourceTypes } from '@/api'
import { ResourceCard } from '@/components/cards'
import {
    ErrorState,
    Loading,
    PageHead,
    PageShell,
    PrincipleHint,
    Term,
    Tooltip,
} from '@/components/common'
import { useAsync, useDebounced } from '@/hooks'
import { useI18n } from '@/i18n'
import type { Scope } from '@/types'

const scopes: Scope[] = ['global', 'european', 'national', 'institutional']

export function Resources() {
    const { t, localized, scopeName } = useI18n()
    const [params, setParams] = useSearchParams()

    const query = params.get('q') ?? ''
    const selectedTypes = params.getAll('types')
    const selectedScopes = params.getAll('scopes') as Scope[]
    const selectedPrinciples = params.getAll('principles')
    const selectedFip = params.get('fips') ?? ''
    const selectedVerified = params.get('verified') ?? ''
    const debouncedQuery = useDebounced(query)

    const types = useAsync(fetchResourceTypes, [])
    const principles = useAsync(fetchPrinciples, [])
    const fips = useAsync(fetchFips, [])

    const typeKey = selectedTypes.join(',')
    const scopeKey = selectedScopes.join(',')
    const principleKey = selectedPrinciples.join(',')

    const filters = useMemo(
        () => ({
            q: debouncedQuery,
            types: typeKey ? typeKey.split(',') : [],
            scopes: scopeKey ? (scopeKey.split(',') as Scope[]) : [],
            principles: principleKey ? principleKey.split(',') : [],
            fips: selectedFip ? [selectedFip] : [],
            verified: selectedVerified ? selectedVerified === 'true' : undefined,
        }),
        [debouncedQuery, typeKey, scopeKey, principleKey, selectedFip, selectedVerified],
    )

    const resources = useAsync(() => fetchResources(filters), [filters])

    const update = (key: string, values: string[]) => {
        const next = new URLSearchParams(params)
        next.delete(key)
        for (const value of values) {
            next.append(key, value)
        }
        setParams(next, { replace: true })
    }

    const toggle = (key: string, value: string, current: string[]) => {
        update(
            key,
            current.includes(value)
                ? current.filter((item) => item !== value)
                : [...current, value],
        )
    }

    const hasFilters =
        Boolean(query) ||
        selectedTypes.length > 0 ||
        selectedScopes.length > 0 ||
        selectedPrinciples.length > 0 ||
        Boolean(selectedFip) ||
        Boolean(selectedVerified)

    return (
        <PageShell>
            <PageHead title={t('resources.title')} lead={t('resources.lead')} term="resource" />

            <div className="filters card">
                <div className="filters__row">
                    <div className="field field--grow">
                        <label htmlFor="resource-search">{t('common.search')}</label>
                        <input
                            id="resource-search"
                            type="search"
                            value={query}
                            placeholder={t('common.searchPlaceholder')}
                            onChange={(event) =>
                                update('q', event.target.value ? [event.target.value] : [])
                            }
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="resource-fip">{t('resources.filter.fip')}</label>
                        <select
                            id="resource-fip"
                            value={selectedFip}
                            onChange={(event) =>
                                update('fips', event.target.value ? [event.target.value] : [])
                            }
                        >
                            <option value="">—</option>
                            {(fips.data ?? []).map((fip) => (
                                <option key={fip.id} value={fip.id}>
                                    {fip.acronym ?? localized(fip.title)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label>
                        {t('resources.filter.type')} <Term id="category" />
                    </label>
                    <div className="toggle-group">
                        {(types.data ?? []).map((type) => (
                            <Tooltip key={type.id} content={localized(type.description)}>
                                <button
                                    type="button"
                                    className="toggle"
                                    aria-pressed={selectedTypes.includes(type.id)}
                                    onClick={() => toggle('types', type.id, selectedTypes)}
                                >
                                    {type.icon} {localized(type.label)}
                                </button>
                            </Tooltip>
                        ))}
                    </div>
                </div>

                <div className="filters__row">
                    <div className="field field--grow">
                        <label>
                            {t('resources.filter.principle')} <Term id="principle" />
                        </label>
                        <div className="toggle-group">
                            {(principles.data ?? []).map((principle) => (
                                <Tooltip
                                    key={principle.id}
                                    content={<PrincipleHint principle={principle} />}
                                >
                                    <button
                                        type="button"
                                        className="toggle"
                                        aria-pressed={selectedPrinciples.includes(principle.id)}
                                        onClick={() =>
                                            toggle('principles', principle.id, selectedPrinciples)
                                        }
                                    >
                                        {principle.id}
                                    </button>
                                </Tooltip>
                            ))}
                        </div>
                    </div>
                    <div className="field">
                        <label>
                            {t('verification.filter')} <Term id="verification" />
                        </label>
                        <div className="toggle-group">
                            {[
                                ['true', t('verification.verifiedOnly')],
                                ['false', t('verification.unverifiedOnly')],
                            ].map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    className="toggle"
                                    aria-pressed={selectedVerified === value}
                                    onClick={() =>
                                        update(
                                            'verified',
                                            selectedVerified === value ? [] : [value],
                                        )
                                    }
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="field">
                        <label>
                            {t('resources.filter.scope')} <Term id="scope" />
                        </label>
                        <div className="toggle-group">
                            {scopes.map((scope) => (
                                <button
                                    key={scope}
                                    type="button"
                                    className="toggle"
                                    aria-pressed={selectedScopes.includes(scope)}
                                    onClick={() => toggle('scopes', scope, selectedScopes)}
                                >
                                    {scopeName(scope)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {hasFilters ? (
                    <div>
                        <button
                            className="button"
                            type="button"
                            onClick={() => setParams(new URLSearchParams(), { replace: true })}
                        >
                            {t('common.clear')}
                        </button>
                    </div>
                ) : null}
            </div>

            {resources.error ? (
                <ErrorState error={resources.error} onRetry={resources.reload} />
            ) : resources.loading && !resources.data ? (
                <Loading />
            ) : (
                <>
                    <p className="results-count">
                        {resources.data?.length ?? 0} {t('resources.count')}
                    </p>
                    {resources.data?.length ? (
                        <div className="grid grid--cards">
                            {resources.data.map((resource) => (
                                <ResourceCard key={resource.id} resource={resource} />
                            ))}
                        </div>
                    ) : (
                        <p className="empty">{t('common.noResults')}</p>
                    )}
                </>
            )}
        </PageShell>
    )
}
